/* P.Louise — Bundle Builder By Step */
(function () {
  function formatMoney(cents, format) {
    const value = (cents / 100).toFixed(2);
    const parts = value.split('.');
    const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const decimal = parts[1];
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const match = (format || '£{{amount}}').match(placeholder);
    const key = match ? match[1] : 'amount';

    const amount = key === 'amount_no_decimals' ? whole
      : key === 'amount_with_comma_separator' ? `${whole},${decimal}`
      : key === 'amount_no_decimals_with_comma_separator' ? whole
      : `${whole}.${decimal}`;

    return (format || '£{{amount}}').replace(placeholder, amount);
  }

  class StepBundleBuilder {
    constructor(root) {
      this.root = root;
      this.money = (c) => formatMoney(c, root.dataset.moneyFormat);
      this.bundlePrice = parseInt(root.dataset.bundlePrice, 10) || 0;
      this.lockSteps = root.dataset.lockSteps === 'true';
      this.useNextStepButton = root.dataset.nextStepButton === 'true';
      this.nextStepLabel = root.dataset.nextStepLabel || 'Continue to next step';
      this.finalStepLabel = root.dataset.finalStepLabel || 'Review your bundle';
      this.bundleName = root.dataset.bundleName || 'Step Bundle';

      this.steps = Array.from(root.querySelectorAll('[data-sbs-step]'));
      this.cards = Array.from(root.querySelectorAll('[data-sbs-card]'));
      this.panel = root.querySelector('[data-sbs-panel]');
      this.peekToggle = root.querySelector('[data-sbs-peek-toggle]');
      this.peekCta = root.querySelector('[data-sbs-peek-cta]');
      this.progress = root.querySelector('[data-sbs-progress]');
      this.summarySteps = root.querySelector('[data-sbs-summary-steps]');
      this.picksEl = root.querySelector('[data-sbs-picks]');
      this.priceEl = root.querySelector('[data-sbs-price]');
      this.cta = root.querySelector('[data-sbs-cta]');
      this.toast = root.querySelector('[data-sbs-toast]');
      this.toastInner = root.querySelector('[data-sbs-toast-inner]');
      this.addonsWrap = root.querySelector('[data-sbs-addons]');
      this.addons = Array.from(root.querySelectorAll('[data-sbs-addon]'));
      this.addonCheckboxes = Array.from(root.querySelectorAll('[data-sbs-addon-checkbox]'));

      this.picks = [];

      this.bind();
      this.render();
      this.openFirstIncompleteStep();
      this.updatePanelPad();

      window.addEventListener('resize', () => this.updatePanelPad());
    }

    bind() {
      this.steps.forEach((step) => {
        const toggle = step.querySelector('[data-sbs-step-toggle]');
        const body = step.querySelector('[data-sbs-step-body]');
        const nextButton = step.querySelector('[data-sbs-next-step]');

        if (toggle && body) {
          toggle.addEventListener('click', () => {
            if (!this.isStepUnlocked(step.dataset.stepIndex)) {
              this.showToast('Complete the previous step first.');
              return;
            }

            const open = body.hidden;

            this.steps.forEach((otherStep) => {
              const otherBody = otherStep.querySelector('[data-sbs-step-body]');
              const otherToggle = otherStep.querySelector('[data-sbs-step-toggle]');
              if (otherBody) otherBody.hidden = true;
              if (otherToggle) otherToggle.setAttribute('aria-expanded', 'false');
            });

            body.hidden = !open;
            toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
          });
        }

        if (nextButton) {
          nextButton.addEventListener('click', () => {
            this.openNextIncompleteStep(step.dataset.stepIndex, true);
          });
        }
      });

      this.cards.forEach((card) => {
        card.addEventListener('click', (e) => {
          if (card.hasAttribute('data-unavailable')) return;

          if (!this.isStepUnlocked(card.dataset.stepIndex)) {
            this.showToast('Complete the previous step first.');
            return;
          }

          const select = card.querySelector('[data-sbs-variant]');
          if (select && select.value !== card.dataset.variantId) this.changeVariant(select);

          if (e.target.closest('[data-sbs-add]') || e.target.closest('[data-sbs-plus]')) {
            this.add(card);
            return;
          }

          if (e.target.closest('[data-sbs-minus]')) {
            this.removeOne(card.dataset.variantId, card.dataset.stepIndex);
          }
        });

        const select = card.querySelector('[data-sbs-variant]');
        if (select) select.addEventListener('change', () => this.changeVariant(select));
      });

      if (this.peekToggle) {
        this.peekToggle.addEventListener('click', () => {
          const open = this.panel.classList.toggle('is-open');
          this.peekToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
          this.updatePanelPad();
        });
      }

      this.addonCheckboxes.forEach((checkbox) => {
        checkbox.addEventListener('change', () => this.renderFooter());
      });

      this.cta.addEventListener('click', () => this.addToCart());
    }

    updatePanelPad() {
      if (!this.panel) return;

      if (!window.matchMedia('(max-width: 959px)').matches) {
        this.root.style.setProperty('--sbs-panel-pad', '0px');
        return;
      }

      if (!this.panel.classList.contains('is-open')) this.collapsedHeight = this.panel.offsetHeight;
      this.root.style.setProperty('--sbs-panel-pad', `${this.collapsedHeight || 0}px`);
    }

    getStep(stepIndex) {
      return this.steps.find((step) => step.dataset.stepIndex === String(stepIndex));
    }

    getStepTitle(stepIndex) {
      const step = this.getStep(stepIndex);
      return step ? (step.querySelector('.sbs-step__title')?.textContent || `Step ${stepIndex}`) : `Step ${stepIndex}`;
    }

    stepRequired(stepIndex) {
      const step = this.getStep(stepIndex);
      return step ? parseInt(step.dataset.required, 10) || 0 : 0;
    }

    stepCount(stepIndex) {
      return this.picks.filter((pick) => pick.stepIndex === String(stepIndex)).length;
    }

    isStepComplete(stepIndex) {
      return this.stepCount(stepIndex) >= this.stepRequired(stepIndex);
    }

    isStepUnlocked(stepIndex) {
      if (!this.lockSteps) return true;

      const targetIndex = this.steps.findIndex((step) => step.dataset.stepIndex === String(stepIndex));
      if (targetIndex <= 0) return true;

      return this.steps.slice(0, targetIndex).every((step) => this.isStepComplete(step.dataset.stepIndex));
    }

    totalRequired() {
      return this.steps.reduce((sum, step) => sum + (parseInt(step.dataset.required, 10) || 0), 0);
    }

    totalPicked() {
      return this.picks.length;
    }

    countOf(variantId, stepIndex) {
      return this.picks.filter((pick) => pick.variantId === String(variantId) && pick.stepIndex === String(stepIndex)).length;
    }

    changeVariant(select) {
      const card = select.closest('[data-sbs-card]');
      if (!card) return;

      const oldId = card.dataset.variantId;
      const option = select.options[select.selectedIndex];

      card.dataset.variantId = select.value;
      card.dataset.price = option.dataset.price || card.dataset.price;

      if (option.dataset.image) card.dataset.image = option.dataset.image;

      if (option.dataset.cardImage) {
        const img = card.querySelector('.sbs-card__media img');
        if (img) img.src = option.dataset.cardImage;
      }

      this.picks.forEach((pick) => {
        if (pick.variantId === oldId && pick.stepIndex === card.dataset.stepIndex) {
          pick.variantId = card.dataset.variantId;
          pick.price = parseInt(card.dataset.price, 10) || 0;
          pick.image = card.dataset.image || '';
          pick.name = card.dataset.name || '';
        }
      });

      this.render();
    }

    add(card) {
      const stepIndex = card.dataset.stepIndex;
      const required = this.stepRequired(stepIndex);
      const current = this.stepCount(stepIndex);

      if (current >= required) {
        this.showToast(`Step ${stepIndex} is complete — remove one to change it.`);
        return;
      }

      this.picks.push({
        stepIndex,
        stepTitle: card.dataset.stepTitle || this.getStepTitle(stepIndex),
        variantId: card.dataset.variantId,
        name: card.dataset.name || '',
        price: parseInt(card.dataset.price, 10) || 0,
        image: card.dataset.image || ''
      });

      this.render();

      if (this.stepCount(stepIndex) >= required && !this.useNextStepButton) {
        this.openNextIncompleteStep(stepIndex, false);
      }
    }

    removeOne(variantId, stepIndex) {
      const index = this.picks.map((pick) => `${pick.variantId}::${pick.stepIndex}`).lastIndexOf(`${variantId}::${stepIndex}`);
      if (index < 0) return;

      this.picks.splice(index, 1);
      this.render();
    }

    removeAt(index) {
      this.picks.splice(index, 1);
      this.render();
      this.openFirstIncompleteStep();
    }

    isComplete() {
      return this.steps.length > 0 && this.steps.every((step) => this.isStepComplete(step.dataset.stepIndex));
    }

    openStep(stepIndex) {
      if (!this.isStepUnlocked(stepIndex)) {
        this.showToast('Complete the previous step first.');
        return;
      }

      this.steps.forEach((step) => {
        const body = step.querySelector('[data-sbs-step-body]');
        const toggle = step.querySelector('[data-sbs-step-toggle]');
        const open = step.dataset.stepIndex === String(stepIndex);

        if (body) body.hidden = !open;
        if (toggle) toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      });

      const target = this.getStep(stepIndex);
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    openFirstIncompleteStep() {
      const target = this.steps.find((step) => {
        return this.isStepUnlocked(step.dataset.stepIndex) && !this.isStepComplete(step.dataset.stepIndex);
      });

      if (target) this.openStep(target.dataset.stepIndex);
    }

    openNextIncompleteStep(currentStepIndex, userTriggered) {
      const currentNum = parseInt(currentStepIndex, 10);
      const next = this.steps.find((step) => {
        const num = parseInt(step.dataset.stepIndex, 10);
        return num > currentNum && this.isStepUnlocked(step.dataset.stepIndex) && !this.isStepComplete(step.dataset.stepIndex);
      });

      if (next) {
        this.openStep(next.dataset.stepIndex);
        return;
      }

      if (this.isComplete() && this.panel && this.peekToggle && window.matchMedia('(max-width: 959px)').matches) {
        this.panel.classList.add('is-open');
        this.peekToggle.setAttribute('aria-expanded', 'true');
        this.updatePanelPad();
      }

      void userTriggered;
    }

    selectedAddonTotal() {
      return this.addons.reduce((sum, addon) => {
        const checkbox = addon.querySelector('[data-sbs-addon-checkbox]');
        if (!checkbox || !checkbox.checked || checkbox.disabled) return sum;
        return sum + (parseInt(addon.dataset.addonPrice, 10) || 0);
      }, 0);
    }

    renderCards() {
      this.cards.forEach((card) => {
        const stepIndex = card.dataset.stepIndex;
        const stepFull = this.stepCount(stepIndex) >= this.stepRequired(stepIndex);
        const count = this.countOf(card.dataset.variantId, stepIndex);
        const selected = count > 0;
        const unlocked = this.isStepUnlocked(stepIndex);

        card.classList.toggle('is-selected', selected);
        card.classList.toggle('is-locked', !unlocked || (stepFull && !selected));

        const countEl = card.querySelector('[data-sbs-count]');
        if (countEl) countEl.textContent = count;

        const add = card.querySelector('[data-sbs-add]');
        const plus = card.querySelector('[data-sbs-plus]');

        if (add) add.disabled = card.hasAttribute('data-unavailable') || !unlocked || stepFull;
        if (plus) plus.disabled = card.hasAttribute('data-unavailable') || !unlocked || stepFull;
      });
    }

    renderSteps() {
      this.steps.forEach((step) => {
        const stepIndex = step.dataset.stepIndex;
        const required = parseInt(step.dataset.required, 10) || 0;
        const count = this.stepCount(stepIndex);
        const complete = count >= required;
        const unlocked = this.isStepUnlocked(stepIndex);

        step.classList.toggle('is-complete', complete);
        step.classList.toggle('is-locked', !unlocked);

        const status = step.querySelector('[data-sbs-step-status]');
        if (status) {
          if (!unlocked) status.textContent = 'Locked';
          else status.textContent = complete ? 'Complete ✓' : `${count} / ${required}`;
        }

        const nextButton = step.querySelector('[data-sbs-next-step]');
        if (nextButton) {
          const hasNext = this.steps.some((otherStep) => parseInt(otherStep.dataset.stepIndex, 10) > parseInt(stepIndex, 10));
          nextButton.hidden = !this.useNextStepButton || !complete || !unlocked;
          nextButton.textContent = hasNext ? this.nextStepLabel : this.finalStepLabel;
        }
      });
    }

    renderSummarySteps() {
      if (!this.summarySteps) return;

      this.summarySteps.innerHTML = '';

      this.steps.forEach((step) => {
        const stepIndex = step.dataset.stepIndex;
        const required = parseInt(step.dataset.required, 10) || 0;
        const count = this.stepCount(stepIndex);
        const complete = count >= required;
        const unlocked = this.isStepUnlocked(stepIndex);
        const title = this.getStepTitle(stepIndex);

        const row = document.createElement('button');
        row.type = 'button';
        row.className = 'sbs-summary-step';
        row.classList.toggle('is-complete', complete);
        row.classList.toggle('is-locked', !unlocked);
        row.innerHTML = `<span>${title}</span><span>${complete ? '✓' : `${count}/${required}`}</span>`;
        row.addEventListener('click', () => this.openStep(stepIndex));

        this.summarySteps.appendChild(row);
      });
    }

    renderPicks() {
      if (!this.picksEl) return;

      this.picksEl.innerHTML = '';

      this.picks.forEach((pick, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'sbs-pick';
        button.setAttribute('aria-label', `Remove ${pick.name || 'item'}`);

        const img = document.createElement('span');
        img.className = 'sbs-pick__img';
        if (pick.image) img.style.backgroundImage = `url('${pick.image}')`;

        const remove = document.createElement('span');
        remove.className = 'sbs-pick__remove';
        remove.textContent = '×';

        button.appendChild(img);
        button.appendChild(remove);
        button.addEventListener('click', () => this.removeAt(index));

        this.picksEl.appendChild(button);
      });
    }

    renderAddons() {
      const complete = this.isComplete();

      if (this.addonsWrap) {
        this.addonsWrap.classList.toggle('is-visible', complete && this.addons.length > 0);
      }

      this.addonCheckboxes.forEach((checkbox) => {
        checkbox.disabled = !complete || checkbox.closest('[data-sbs-addon]')?.hasAttribute('data-addon-unavailable');
        if (!complete) checkbox.checked = false;
      });
    }

    renderFooter() {
      const complete = this.isComplete();
      const picked = this.totalPicked();
      const required = this.totalRequired();
      const addonTotal = this.selectedAddonTotal();
      const displayPrice = this.bundlePrice + addonTotal;

      this.renderAddons();

      if (this.priceEl) this.priceEl.textContent = this.money(displayPrice);

      if (this.progress) {
        if (complete) {
          this.progress.textContent = `Bundle ready — ${picked}/${required} products`;
        } else {
          const currentStep = this.steps.find((step) => this.isStepUnlocked(step.dataset.stepIndex) && !this.isStepComplete(step.dataset.stepIndex));
          if (currentStep) {
            const stepIndex = currentStep.dataset.stepIndex;
            const count = this.stepCount(stepIndex);
            const stepRequired = this.stepRequired(stepIndex);
            const title = this.getStepTitle(stepIndex);
            this.progress.textContent = `Step ${stepIndex} of ${this.steps.length}: ${title} · ${count}/${stepRequired}`;
          } else {
            this.progress.textContent = `Add ${required - picked} more → ${picked}/${required} products`;
          }
        }
      }

      if (this.peekCta) this.peekCta.hidden = !complete;

      this.cta.disabled = !complete;
      this.cta.textContent = complete ? `Add bundle · ${this.money(displayPrice)}` : 'Complete all steps';
    }

    render() {
      this.renderCards();
      this.renderSteps();
      this.renderSummarySteps();
      this.renderPicks();
      this.renderFooter();
      this.updatePanelPad();
    }

    showToast(message) {
      if (!this.toast || !this.toastInner) return;

      this.toastInner.textContent = message;
      this.toast.classList.add('is-visible');

      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => this.toast.classList.remove('is-visible'), 2400);
    }

    addToCart() {
      if (!this.isComplete() || this.cta.disabled) return;

      const grouped = {};

      this.picks.forEach((pick) => {
        const key = `${pick.variantId}::${pick.stepIndex}::${pick.stepTitle}`;
        if (!grouped[key]) {
          grouped[key] = {
            id: parseInt(pick.variantId, 10),
            quantity: 0,
            stepIndex: pick.stepIndex,
            stepTitle: pick.stepTitle
          };
        }
        grouped[key].quantity += 1;
      });

      const items = Object.values(grouped).map((item) => ({
        id: item.id,
        quantity: item.quantity,
        properties: {
          '_Bundle type': 'Step Builder',
          '_Bundle name': this.bundleName,
          '_Step bundle': 'Yes',
          '_Step': `Step ${item.stepIndex}`,
          '_Step title': item.stepTitle
        }
      }));

      this.addons.forEach((addon) => {
        const checkbox = addon.querySelector('[data-sbs-addon-checkbox]');
        const variantId = parseInt(addon.dataset.addonVariantId, 10);

        if (!checkbox || !checkbox.checked || checkbox.disabled || !variantId) return;

        items.push({
          id: variantId,
          quantity: 1,
          properties: {
            '_Bundle type': 'Step Builder',
            '_Bundle name': this.bundleName,
            '_Bundle add-on': 'Yes'
          }
        });
      });

      const original = this.cta.textContent;
      this.cta.disabled = true;
      this.cta.textContent = 'Adding…';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items })
      })
        .then((response) => {
          if (!response.ok) return response.json().then((err) => Promise.reject(err));
          return response.json();
        })
        .then((data) => {
          this.picks = [];
          this.addonCheckboxes.forEach((checkbox) => { checkbox.checked = false; });
          this.render();
          this.openFirstIncompleteStep();
          this.showToast('Bundle added to your bag! 💕');
          document.dispatchEvent(new CustomEvent('sbs:added-to-cart', { detail: data, bubbles: true }));
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        })
        .catch((err) => {
          this.showToast((err && err.description) || 'Something went wrong — please try again.');
          this.cta.disabled = false;
          this.cta.textContent = original;
        });
    }
  }

  function init() {
    document.querySelectorAll('[data-step-bundle-builder]').forEach((root) => {
      if (root.__sbsInitialised) return;
      root.__sbsInitialised = true;
      new StepBundleBuilder(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
